import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { CloudFrameDataTable } from "@cloudframe/data-table";
import { CloudFrameDropdown } from "@cloudframe/dropdown";
import { CloudFramePrimaryButton } from "@cloudframe/button";
import { CloudFrameTextBox } from "@cloudframe/textbox";
import {
  ConstrainMode,
  DetailsListLayoutMode,
  IColumn,
  IDropdownOption,
  IIconProps,
  SelectionMode,
} from "@fluentui/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ActionButtons from "../action-buttons/action-buttons";
import { UserPermission } from "../../constants/permissions.constants";
import {
  QUERY_GET_ALL_CONTINUUM_LICENSES,
  QUERY_GET_ALL_SCANNER_LICENSES,
  QUERY_GET_CONTINUUM_LICENSE_TEXT,
  QUERY_GET_SCANNER_LICENSE_TEXT,
} from "../../graphql-query/continuum-scanner-license.query";
import {
  MUTATION_APPROVE_CONTINUUM_LICENSE,
  MUTATION_DECLINE_CONTINUUM_LICENSE,
} from "../../graphql-mutations/continuum-scanner-license.mutation";
import { saveStringAsTxt } from "../../utils/file.utils";
import { getFormattedDate } from "../../utils/date.utils";
import ContinuumScannerLicenseDetailModal from "./continuum-scanner-license-detail-modal";
import { useStyles } from "./continuum-scanner-license-list.styles";
import { findLicenseByQueryParamId, formatLicenseTypeLabel } from "./continuum-scanner-license-list.utils";

const PRODUCT_FILTER_OPTIONS: IDropdownOption[] = [
  { key: "All", text: "All" },
  { key: "Continuum", text: "Continuum" },
  { key: "Scanner", text: "Scanner" },
  { key: "CodeNavigator", text: "CodeNavigator" },
];

const STATUS_FILTER_OPTIONS: IDropdownOption[] = [
  { key: "All", text: "All" },
  { key: "PENDING", text: "Pending" },
  { key: "ACCEPTED", text: "Accepted" },
  { key: "ACTIVE", text: "Active" },
  { key: "DECLINED", text: "Declined" },
  { key: "DEACTIVATION_REQUESTED", text: "Deactivation Requested" },
  { key: "DEACTIVATED", text: "Deactivated" },
  { key: "EXPIRED", text: "Expired" },
];

const STATUS_PILL_CLASS_BY_STATUS: Record<string, string> = {
  ACTIVE: "statusActive",
  PENDING: "statusPending",
  ACCEPTED: "statusAccepted",
  DECLINED: "statusDeclined",
  DEACTIVATION_REQUESTED: "statusDeactivationRequested",
  DEACTIVATED: "statusDeactivated",
  EXPIRED: "statusExpired",
};

const searchIcon: IIconProps = { iconName: "Search" };

const ContinuumScannerLicenseList = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlightLicenseId, setHighlightLicenseId] = useState<string | null>(
    (location.state as any)?.highlightLicenseId ?? null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [productFilter, setProductFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [selectedLicense, setSelectedLicense] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!highlightLicenseId) return;
    const timer = setTimeout(() => setHighlightLicenseId(null), 3000);
    window.history.replaceState({}, document.title);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: continuumData,
    loading: continuumLoading,
    refetch: refetchContinuum,
  } = useQuery(QUERY_GET_ALL_CONTINUUM_LICENSES);
  const {
    data: scannerData,
    loading: scannerLoading,
    refetch: refetchScanner,
  } = useQuery(QUERY_GET_ALL_SCANNER_LICENSES);
  const [loadContinuumLicenseText] = useLazyQuery(QUERY_GET_CONTINUUM_LICENSE_TEXT);
  const [loadScannerLicenseText] = useLazyQuery(QUERY_GET_SCANNER_LICENSE_TEXT);
  const [approveContinuumLicense] = useMutation(MUTATION_APPROVE_CONTINUUM_LICENSE);
  const [declineContinuumLicense] = useMutation(MUTATION_DECLINE_CONTINUUM_LICENSE);

  const allRows = useMemo(() => {
    const continuumRows = (continuumData?.getAllContinuumLicenses || []).map(
      (l: any) => ({
        ...l,
        product: l.product || "Continuum",
        effectiveDate: l.platformEffectiveDate,
        expiryDate: l.platformExpiryDate,
      })
    );
    const scannerRows = (scannerData?.getAllScannerLicenses || []).map(
      (l: any) => ({
        ...l,
        product: "Scanner",
        effectiveDate: l.scannerEffectiveDate,
        expiryDate: l.scannerExpiryDate,
      })
    );
    return [...continuumRows, ...scannerRows];
  }, [continuumData, scannerData]);

  useEffect(() => {
    // "lid" matches the query param used by the approval-email deep link for
    // every other license type (see license-list.tsx); "licenseId" is kept
    // as a fallback for any other internal caller of this route.
    const queryLicenseId = searchParams.get("lid") || searchParams.get("licenseId");
    if (!queryLicenseId || allRows.length === 0) return;
    const match = findLicenseByQueryParamId(allRows, queryLicenseId);
    if (match) {
      setSelectedLicense(match);
      setShowDetailModal(true);
      setSearchParams(undefined);
    }
  }, [allRows, searchParams, setSearchParams]);

  const rows = useMemo(() => {
    let filtered = allRows;
    if (productFilter !== "All") {
      filtered = filtered.filter((l) => l.product === productFilter);
    }
    if (statusFilter !== "All") {
      filtered = filtered.filter((l) => l.licenseStatus === statusFilter);
    }
    if (searchText.trim()) {
      const text = searchText.trim().toLowerCase();
      filtered = filtered.filter(
        (l) =>
          `${l.customerName}`.toLowerCase().includes(text) ||
          `${l.licenseId}`.toLowerCase().includes(text)
      );
    }
    return [...filtered].sort((a, b) => {
      const aTime = a.effectiveDate ? new Date(a.effectiveDate).getTime() : 0;
      const bTime = b.effectiveDate ? new Date(b.effectiveDate).getTime() : 0;
      return bTime - aTime;
    });
  }, [allRows, productFilter, statusFilter, searchText]);

  const onViewLicense = (row: any) => {
    setSelectedLicense(row);
    setShowDetailModal(true);
  };

  const onDownloadLicense = async (row: any) => {
    if (!row?.licenseId) return;
    const loadText =
      row.product !== "Scanner" ? loadContinuumLicenseText : loadScannerLicenseText;
    const { data } = await loadText({ variables: { licenseId: row.licenseId } });
    const licenseText =
      data?.getContinuumLicenseText?.licenseText ||
      data?.getScannerLicenseText?.licenseText;
    if (licenseText) {
      saveStringAsTxt(licenseText, "Cloudframe_License");
    }
  };

  const onManageKeys = (row: any) => {
    if (!row?.licenseId) return;
    navigate(`/continuum-machine-activation/${row.licenseId}`);
  };

  const onApproveLicense = async (): Promise<void> => {
    if (!selectedLicense?.id) return;
    await approveContinuumLicense({ variables: { id: selectedLicense.id } });
    await refetchContinuum();
    setShowDetailModal(false);
  };

  const onDeclineLicense = async (declineReason: string): Promise<void> => {
    if (!selectedLicense?.id) return;
    await declineContinuumLicense({
      variables: { id: selectedLicense.id, declineReason },
    });
    await refetchContinuum();
    setShowDetailModal(false);
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refetchContinuum(), refetchScanner()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const columns: IColumn[] = [
    { key: "product", name: "Product", fieldName: "product", minWidth: 90, maxWidth: 130 },
    { key: "licenseId", name: "License ID", fieldName: "licenseId", minWidth: 110, maxWidth: 150 },
    { key: "customerName", name: "Customer", fieldName: "customerName", minWidth: 110, maxWidth: 160 },
    { key: "department", name: "Department", fieldName: "department", minWidth: 100, maxWidth: 150 },
    {
      key: "licenseType",
      name: "License Type",
      fieldName: "licenseType",
      minWidth: 100,
      maxWidth: 140,
      onRender: (item: any) => <span>{formatLicenseTypeLabel(item.licenseType)}</span>,
    },
    {
      key: "effectiveDate",
      name: "Issued Date",
      fieldName: "effectiveDate",
      minWidth: 110,
      maxWidth: 150,
      onRender: (item: any) =>
        item.effectiveDate ? (
          <span>{getFormattedDate(item.effectiveDate)}</span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      key: "expiryDate",
      name: "Expiry Date",
      fieldName: "expiryDate",
      minWidth: 110,
      maxWidth: 150,
      onRender: (item: any) =>
        item.expiryDate ? (
          <span>{getFormattedDate(item.expiryDate)}</span>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      key: "jiraTicket",
      name: "Ticket",
      fieldName: "jiraTicket",
      minWidth: 90,
      maxWidth: 130,
      onRender: (item: any) =>
        item.jiraTicket ? (
          <a
            target="_blank"
            rel="noreferrer"
            href={item.jiraTicket}
            style={{ textDecoration: "none" }}
          >
            View ticket
          </a>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      key: "licenseStatus",
      name: "Status",
      fieldName: "licenseStatus",
      minWidth: 90,
      maxWidth: 130,
      onRender: (item: any) => {
        const pillClass =
          (classes as any)[STATUS_PILL_CLASS_BY_STATUS[item.licenseStatus]] || "";
        return (
          <span className={`${classes.statusPill} ${pillClass}`}>
            {item.licenseStatus}
          </span>
        );
      },
    },
    {
      key: "action",
      name: "Action",
      minWidth: 140,
      onRender: (item: any) => (
        <div className={classes.actionCell}>
          <ActionButtons
            row={item}
            onView={onViewLicense}
            onDownload={onDownloadLicense}
            onManageKeys={item.product !== "Scanner" ? onManageKeys : undefined}
            buttonTitles={{
              viewTitle: "View License",
              downloadTitle: "Download License",
              manageKeysTitle:
                item.product !== "Scanner" ? "Manage Keys" : undefined,
            }}
            permissionKeyOverrides={{
              view: UserPermission.P_CONTINUUM_VIEW_LICENSE,
              download: UserPermission.P_CONTINUUM_DOWNLOAD_LICENSE,
              manageKeys: UserPermission.P_CONTINUUM_MANAGE_KEYS,
            }}
          />
        </div>
      ),
    },
  ];

  const loading = continuumLoading || scannerLoading;

  return (
    <div className={classes.card}>
      <h2 className={classes.title}>Continuum &amp; Scanner Licenses</h2>
      <div className={classes.filterRow}>
        <CloudFrameTextBox
          label="Search"
          placeholder="Search by customer name or license ID"
          iconProps={searchIcon}
          style={{ width: "20rem" }}
          styles={{ fieldGroup: { height: 40, borderRadius: 6 } }}
          onChange={(_, value) => setSearchText(value || "")}
        />
        <CloudFrameDropdown
          label="Status"
          selectedKey={statusFilter}
          options={STATUS_FILTER_OPTIONS}
          onChange={(_, option) => setStatusFilter(option?.key as string)}
          style={{ minWidth: 160 }}
          styles={{ title: { height: 40, lineHeight: "38px", borderRadius: 6 }, caretDownWrapper: { height: 40, lineHeight: "40px" } }}
        />
        <CloudFrameDropdown
          label="Filter by Product"
          selectedKey={productFilter}
          options={PRODUCT_FILTER_OPTIONS}
          onChange={(_, option) => setProductFilter(option?.key as string)}
          style={{ minWidth: 180 }}
          styles={{ title: { height: 40, lineHeight: "38px", borderRadius: 6 }, caretDownWrapper: { height: 40, lineHeight: "40px" } }}
        />
        <CloudFramePrimaryButton
          title="Refresh List"
          text={isRefreshing ? "Refreshing..." : "Refresh"}
          iconProps={{ iconName: "refresh" }}
          disabled={isRefreshing}
          onClick={onRefresh}
          styles={{
            root: {
              minWidth: "auto",
              paddingLeft: "0.75rem",
              paddingRight: "0.75rem",
              height: "40px",
            },
          }}
        />
      </div>
      <div className={classes.tableWrap}>
        <CloudFrameDataTable
          items={rows}
          columns={columns}
          selectionMode={SelectionMode.none}
          constrainMode={ConstrainMode.horizontalConstrained}
          layoutMode={DetailsListLayoutMode.justified}
          onShouldVirtualize={() => false}
          getKey={(item) => `${item.product}-${item.licenseId}`}
          onRenderRow={(props, defaultRender) => {
            if (!props || !defaultRender) return null;
            const isHighlighted =
              !!highlightLicenseId && props.item?.licenseId === highlightLicenseId;
            return defaultRender({
              ...props,
              className: isHighlighted
                ? `${props.className || ""} ${classes.highlightedRow}`.trim()
                : props.className,
            });
          }}
        />
      </div>
      {loading && <div className={classes.mt16}>Loading licenses...</div>}
      {!loading && rows.length === 0 && (
        <div className={classes.mt16}>No licenses found matching your filters.</div>
      )}
      {!loading && (
        <div className={classes.footerRow}>
          Showing {rows.length} of {allRows.length} licenses
        </div>
      )}
      <ContinuumScannerLicenseDetailModal
        isOpen={showDetailModal}
        onDismiss={() => setShowDetailModal(false)}
        license={selectedLicense}
        onApprove={onApproveLicense}
        onDecline={onDeclineLicense}
      />
    </div>
  );
};

export default ContinuumScannerLicenseList;
